
import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Chip, CircularProgress, Paper } from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { MdError } from "react-icons/md";
import apiClient from "../services/apiClient";
import toast from "react-hot-toast";

const ErrorLogs = () => {
  const [errorLogs, setErrorLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  
  const [paginationInfo, setPaginationInfo] = useState({
    total_count: 0,
    total_pages: 0,
  });

  useEffect(() => {
    fetchErrorLogs();
  }, [pagination.pageIndex, pagination.pageSize]);

  const fetchErrorLogs = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/error-logs", {
        page: pagination.pageIndex + 1,
        page_size: pagination.pageSize,
      });

      if (res && res.status && Array.isArray(res.data)) {
        setErrorLogs(res.data);
        setPaginationInfo({
          total_count: res.pagination?.total_count || 0,
          total_pages: res.pagination?.total_pages || 0,
        });
      } else {
        setErrorLogs([]);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching error logs:", error);
      toast.error("Failed to fetch error logs");
      setErrorLogs([]);
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "_id",
        header: "ID",
        size: 150,
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 11 }}>
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: "error_module",
        header: "Module",
        size: 120,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue() || "Unknown"}
            size="small"
            sx={{
              backgroundColor: "#FFF3E0",
              color: "#E65100",
              fontWeight: 600,
            }}
          />
        ),
      },
      {
        accessorKey: "error_message",
        header: "Error Message",
        size: 300,
        Cell: ({ cell }) => (
          <Box>
            <Typography variant="body2" fontWeight={600} color="error.main">
              {cell.getValue() || "No message"}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "error_details",
        header: "Details",
        size: 250,
        Cell: ({ cell }) => (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              maxWidth: "250px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {cell.getValue() || "No details"}
          </Typography>
        ),
      },
      {
        accessorKey: "company_id",
        header: "Company ID",
        size: 150,
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 11 }}>
            {cell.getValue() || "N/A"}
          </Typography>
        ),
      },
      {
        accessorKey: "user_id",
        header: "User ID",
        size: 150,
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 11 }}>
            {cell.getValue() || "N/A"}
          </Typography>
        ),
      },
      {
        accessorKey: "created_at",
        header: "Created At",
        size: 180,
        Cell: ({ cell }) => {
          const date = cell.getValue();
          return (
            <Typography variant="body2" sx={{ fontSize: 12 }}>
              {date ? new Date(date).toLocaleString() : "N/A"}
            </Typography>
          );
        },
      },
      {
        accessorKey: "updated_at",
        header: "Updated At",
        size: 180,
        Cell: ({ cell }) => {
          const date = cell.getValue();
          return (
            <Typography variant="body2" sx={{ fontSize: 12 }}>
              {date ? new Date(date).toLocaleString() : "N/A"}
            </Typography>
          );
        },
      },
    ],
    []
  );

  return (
    <Box sx={{ padding: 3 }}>
      {/* Header Section */}
      <Box
        sx={{
          background: "#667eea",
          borderRadius: "12px",
          p: 3,
          mb: 3,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "12px",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MdError style={{ color: "white", fontSize: 32 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ color: "white", fontWeight: 600 }}>
            Error Logs
          </Typography>
          <Typography variant="body1" sx={{ color: "rgba(255, 255, 255, 0.9)" }}>
            View and monitor system error logs
          </Typography>
        </Box>
      </Box>

      {/* Stats Card */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            p: 3,
            borderRadius: "12px",
            border: "1px solid #E5E7EB",
            background: "#FEE2E2",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: "10px",
                backgroundColor: "rgba(239, 68, 68, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 28, color: "#DC2626" }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: "#DC2626" }}>
                {paginationInfo.total_count}
              </Typography>
              <Typography variant="body2" sx={{ color: "#991B1B", fontWeight: 500 }}>
                Total Error Logs
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Table Section */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <CircularProgress size={48} />
        </Box>
      ) : errorLogs.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "400px",
            gap: 2,
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 64, color: "text.secondary" }} />
          <Typography variant="h6" color="text.secondary">
            No error logs found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            There are no error logs to display at this time.
          </Typography>
        </Box>
      ) : (
        <MaterialReactTable
          columns={columns}
          data={errorLogs}
          manualPagination
          rowCount={paginationInfo.total_count}
          onPaginationChange={setPagination}
          state={{
            isLoading: loading,
            pagination: pagination,
          }}
          enableColumnFilters
          enableGlobalFilter
          enableSorting
          enablePagination
          initialState={{
            sorting: [{ id: "created_at", desc: true }],
          }}
          muiTablePaginationProps={{
            showFirstButton: true,
            showLastButton: true,
          }}
          muiTablePaperProps={{
            elevation: 0,
            sx: {
              borderRadius: 2,
              border: "1px solid #E0E0E0",
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              backgroundColor: "#F9FAFB",
              fontWeight: 600,
            },
          }}
        />
      )}
    </Box>
  );
};

export default ErrorLogs;
